import { Fragment, useCallback, useRef, useMemo, useEffect } from "react";
import { useDataSettings } from "../../hooks/useData";
import { useDataRefObj } from "../../hooks/useDataRef";
import useLatestRef from "../../hooks/useLatestRef";
import { DragAndDrop } from "../../hooks/useDragAndDrop";
import { useFormikContext } from "formik";
import { Form, Button } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlusSquare,
  faBrain,
  faShieldAlt
} from "@fortawesome/free-solid-svg-icons";
import Switch from "../switch";

import cloneDeep from "lodash.clonedeep";
import isEqual from "lodash.isequal";
import set from "lodash.set";
import get from "lodash.get";
import idGen from "../../utils/idGen";

import SubBlock from "./subBlock";
import { subBlockIndex } from "./subBlockIds";
import SelectAll from "../selects/selectAll";
import Results from "./results";
import SelectProperties from "../selects/selectProperties";
import Container from "../container";
import UserPref from "../userPref";

const deleteBlockEvnt = new Event("deleted-block");
const emptyArray = [];
const userStyle = { paddingTop: "10px" };
const styleIconMarginRight = { marginRight: "4px" };
const brainStyle = { color: "green", marginLeft: "6px" };
const shieldStyle = { color: "deeppink", marginLeft: "6px" };
// A new sub-block, with the defaults of results.min and max (a sub-block
// used to get them at its mount, also a stored one without them: the open
// of a page wrote them, see subBlock.jsx)
const emptyBlock = () => ({
  _id: idGen(),
  condition: "*",
  title: "",
  type: "query",
  pinned: true,
  results: { min: 0, max: 20 }
});

const marginTop10 = { marginTop: "10px" };

// The scheduling of a block: the ranges of its scheduled sub-blocks
// (`scheduling`), also by sub-block index (`schedulingMap`), from the
// `scheduled` and `scheduling` of each sub-block.
const scheduleOf = (blocks) => {
  const schedulingMap = [];
  blocks.forEach((block, index) => {
    if (block?.scheduled) schedulingMap.push([index, block.scheduling ?? []]);
  });

  return {
    scheduled: schedulingMap.length > 0,
    scheduling: schedulingMap.flatMap(([, scheduling]) => scheduling ?? []),
    schedulingMap
  };
};

export default function Block(props) {
  const { values, errors, handleChange, handleBlur, setFieldValue } =
    useFormikContext();
  const boundBlocksOnProperty = useDataSettings("boundBlocksOnProperty");
  const typeRef = useRef(new Map());
  const info = useDataRefObj("blocks", values);
  const metadataInPage = useMemo(() => info.metadata, [info.metadata]);
  const valuesRef = useLatestRef(values);

  // The scheduling of the block, computed from the blocks written: it follows
  // the deletes and the moves of the sub-blocks (a map by index kept apart
  // from them did not, and kept the ranges of a deleted sub-block). An edit of
  // a condition always writes it; a delete, an add or a move only if it
  // changes, so that they add no fields to a block without scheduling.
  const writeSchedule = useCallback(
    (blocks, always) => {
      const next = scheduleOf(blocks);
      if (!always) {
        const { scheduled, scheduling, schedulingMap } = valuesRef.current;
        if (
          Boolean(scheduled) === next.scheduled &&
          isEqual(scheduling ?? [], next.scheduling) &&
          isEqual(schedulingMap ?? [], next.schedulingMap)
        )
          return;
      }

      setFieldValue("scheduled", next.scheduled);
      setFieldValue("scheduling", next.scheduling);
      setFieldValue("schedulingMap", next.schedulingMap);
    },
    [setFieldValue, valuesRef]
  );

  // A sub-block may call the dispatch of an older render: the conditions box
  // hands its edit over 1 s after it (debounced), through the callbacks of the
  // render of the edit. So the dispatch does not close over values.blocks: it
  // starts from the blocks of the last render, and an edit of one sub-block
  // writes only that sub-block (blocks[i]), which Formik applies to its
  // latest values. Writing the whole array from the blocks of the render of
  // the edit put back what the other sub-blocks had handed over in the
  // meantime.
  const dispatch = useCallback(
    (action) => {
      const current = valuesRef.current.blocks || [];
      const { type, payload: { blockIndex = 0, data = {} } = {} } = action;

      // The scheduling first: the write of the blocks is the last one Formik
      // validates.
      const writeBlock = (block, index = blockIndex, schedule = false) => {
        const newState = current.slice();
        newState[index] = block;
        if (schedule) writeSchedule(newState, true);
        setFieldValue(`blocks[${index}]`, block);
        return newState;
      };
      const writeBlocks = (newState) => {
        writeSchedule(newState, false);
        setFieldValue("blocks", newState);
        return newState;
      };

      switch (type) {
        case "UPDATE": {
          return writeBlock({ ...cloneDeep(current[blockIndex]), ...data });
        }
        case "UPDATE-INDEX": {
          return writeBlock(data, blockIndex, true);
        }
        // The delayed hand-off of a conditions box: to the sub-block of the
        // edit (blockId: its key, the _id or, in a page, the id), wherever
        // it is now, and to none if it has been deleted. Without a key, to
        // the index of the payload.
        case "UPDATE-CONDITION": {
          const { blockId } = action.payload;
          const index = subBlockIndex(current, blockId, blockIndex);
          if (index < 0) return current.slice();

          return writeBlock({ ...current[index], ...data }, index, true);
        }
        // The write of a field of a sub-block, made 0 ms after the change
        // (updateDeepField): to the sub-block of the change (blockId: its
        // key), wherever it is now, and to none if it has been deleted.
        // Without a key, to the index of the payload.
        case "UPDATE-DEEP": {
          const { path, value, blockId } = action.payload;
          const index = subBlockIndex(current, blockId, blockIndex);
          if (index < 0) return current.slice();

          const block = cloneDeep(current[index]);
          set(block, path, value);
          return writeBlock(block, index);
        }
        case "UPDATE-MULTIPLE": {
          const { path, value } = action.payload;
          const block = cloneDeep(current[blockIndex]);
          path.forEach((p) => set(block, p, value));
          return writeBlock(block);
        }
        case "UPDATE-ALL": {
          return writeBlocks(data);
        }
        case "ADD-EMPTY": {
          const newState = cloneDeep(current);
          newState.splice(blockIndex + 1, 0, emptyBlock());
          return writeBlocks(newState);
        }
        case "DELETE": {
          const newState = cloneDeep(current);
          newState.splice(blockIndex, 1);
          window.dispatchEvent(deleteBlockEvnt);
          return writeBlocks(newState);
        }
        default: {
          return cloneDeep(current);
        }
      }
    },
    [setFieldValue, valuesRef, writeSchedule]
  );

  const blocks = useMemo(() => values.blocks, [values.blocks]);

  const blocksContentTypesRef = useRef();
  const blocksContentTypes = useMemo(() => {
    const types = new Set();
    if (blocks) {
      blocks.forEach((block) => {
        const type = get(block, "results.contentType");
        if (type) types.add(type);
      });
    }

    const newTypes = [...types];

    if (
      blocksContentTypesRef.current &&
      blocksContentTypesRef.current.length > 0
    ) {
      const dif = newTypes.filter((k) =>
        blocksContentTypesRef.current.includes(k)
      );
      if (dif.length === 0) return blocksContentTypesRef.current;
    }

    blocksContentTypesRef.current = newTypes;

    return newTypes;
  }, [blocks]);

  const onTypeChange = useCallback(
    (value, index) => typeRef.current.set(index, value),
    []
  );
  const canBeFallback = useMemo(() => {
    if (info.isFallback || info.isSmart) return true;
    return info.canBeFallback;
  }, [info?.canBeFallback, info?.isFallback, info?.isSmart]);

  // *******************************
  // Statics Handlers
  // *******************************
  const fallbackExcludes = useMemo(() => [values.id], [values.id]);
  const fallbackHandleChange = useCallback(
    (blockId) => setFieldValue("results.fallbackBlockId", blockId),
    [setFieldValue]
  );

  const blockOnTypeChange = useCallback(
    (value, blockIndex) => onTypeChange(value, blockIndex),
    [onTypeChange]
  );

  const handleSmartOrder = useCallback(
    (e) => {
      const newValues = cloneDeep(values);
      setFieldValue("results.smartOrderBlocks", e.target.checked);
      newValues.blocks.forEach((block) => {
        block.pinned = true;
      });
      dispatch({ type: "UPDATE-ALL", payload: { data: newValues.blocks } });
    },
    [dispatch, setFieldValue, values]
  );

  const title = useMemo(() => {
    return (
      <div onClick={(e) => e.stopPropagation()}>
        <Switch
          bsSize="small"
          value={(values.results || {}).flatAllBlocks || false}
          onText="Single Flatten Block"
          onColor="single"
          offText="Multi Block"
          offColor="multiple"
          onChange={(elm, state) => {
            setFieldValue("results.flatAllBlocks", state);
          }}
        />
      </div>
    );
  }, [setFieldValue, values.results]);

  const extra = useMemo(() => {
    return [undefined, false].includes(get(values, "results.flatAllBlocks")) &&
      values.blocks?.length > 2 ? (
      <div className="smart-order-selector-box">
        <Form.Check
          type="switch"
          checked={values.results?.smartOrderBlocks || false}
          value="true"
          onChange={handleSmartOrder}
          onBlur={handleBlur}
          name={`results.smartOrderBlocks`}
          id={`results.smartOrderBlocks`}
          label="Smart Order Blocks"
          disabled={values.results?.shuffleBlocks || false}
        />
        {get(values, "results.smartOrderBlocks") === true && (
          <>
            <UserPref
              style={userStyle}
              values={values.results}
              errors={errors}
              catalog={values.results?.contentType}
              showCatalog={true}
              minified={true}
              onChange={(key, value) => setFieldValue(`results.${key}`, value)}
            />
          </>
        )}
      </div>
    ) : null;
  }, [errors, handleBlur, handleSmartOrder, setFieldValue, values]);

  const boxColor = useMemo(() => {
    return (values.results || {}).flatAllBlocks ? "#111" : "#280138";
  }, [values.results]);

  const enabledPagesSet = useMemo(
    () => new Set(values.enabledPagesId || emptyArray),
    [values.enabledPagesId]
  );
  const handlePagesSelect = useCallback(
    (fSel) => setFieldValue("enabledPagesId", [...fSel]),
    [setFieldValue]
  );

  const blockSelectFilters = useMemo(() => {
    const loopIds = info.loopIds ?? [];

    return {
      catalogs: blocksContentTypes,
      exclude: [...new Set([...fallbackExcludes, ...loopIds])],
      isFallback: true
    };
  }, [blocksContentTypes, fallbackExcludes, info.loopIds]);

  const header = useMemo(() => {
    if (!boundBlocksOnProperty) return null;

    return (
      <div className="property-box">
        <div className="property-label">Properties</div>
        <div className="property-select">
          <SelectProperties
            exclude={emptyArray}
            propertyId={null}
            onlyFolders={true}
            onlyEndpoints={false}
            selected={enabledPagesSet}
            multiple={true}
            onChange={handlePagesSelect}
            placeholder="Select Properties"
          />
        </div>
      </div>
    );
  }, [boundBlocksOnProperty, enabledPagesSet, handlePagesSelect]);

  const footer = useMemo(() => {
    return (
      <Button
        variant="outline-primary"
        style={{ float: "right" }}
        onClick={() =>
          dispatch({
            type: "ADD-EMPTY",
            payload: { blockIndex: blocks ? blocks.length : 0 }
          })
        }
      >
        <FontAwesomeIcon icon={faPlusSquare} style={styleIconMarginRight} /> Add
        Block
      </Button>
    );
  }, [blocks, dispatch]);

  const handleChangeCanBeFallback = useCallback(
    (e) => {
      setFieldValue("canBeFallback", e.target.checked ? true : false);
    },
    [setFieldValue]
  );

  useEffect(() => {
    if (!values.blocks) {
      setFieldValue("results", { flatAllBlocks: true, min: 0 });
      setFieldValue("blocks", [emptyBlock()]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (values?.blocks) {
      if (!blocks) {
        console.log("😱 BIZZARRO DISPATCH");
        dispatch({ type: "UPDATE-ALL", payload: { data: values.blocks } });
      }
    }
  }, [blocks, dispatch, values.blocks]);

  return (
    <>
      <Container
        title={title}
        help="blocks-container"
        color={boxColor}
        coloredBars={false}
        header={header}
        headerOverflow={"visible"}
        headerOnlyOnFolded={false}
        extra={extra}
        footer={footer}
        foldable={true}
        warningMessage={errors.general}
      >
        <DragAndDrop>
          {blocks?.map((block, blockIndex) => {
            return (
              <SubBlock
                key={`block-${blockIndex}`}
                block={block}
                blockIndex={blockIndex}
                blocks={blocks}
                masterId={values.id}
                isFallback={values.canBeFallback}
                info={info}
                smartSortable={get(values, "results.smartOrderBlocks")}
                shuffleable={get(values, "results.shuffleBlocks")}
                flatten={values?.results?.flatAllBlocks}
                dispatch={dispatch}
                onTypeChange={blockOnTypeChange}
              />
            );
          })}
        </DragAndDrop>
      </Container>
      <div className="extra-container">
        <Results info={info} />
        <Container
          title="Extended"
          help="block-global-exteded"
          foldable={false}
          folded={false}
          className="extra-container-window"
        >
          {canBeFallback ? (
            <Fragment>
              <Form.Check
                disabled={info.isUsedAsFallback}
                type="switch"
                checked={Boolean(values.canBeFallback || false)}
                value={true}
                onChange={handleChangeCanBeFallback}
                onBlur={handleBlur}
                name={`canBeFallback`}
                id={`canBeFallback`}
                label={
                  <span>
                    {info.isUsedAsFallback ? "Must be" : "Is"} a <b>Fallback</b>
                    <FontAwesomeIcon icon={faShieldAlt} style={shieldStyle} />
                  </span>
                }
              />
              <Form.Check
                disabled={info.isUsedInSmartBlockContainer}
                type="switch"
                checked={values.canBeSmart || false}
                value="true"
                onChange={handleChange}
                onBlur={handleBlur}
                name={`canBeSmart`}
                id={`canBeSmart`}
                label={
                  <span>
                    {info.isUsedInSmartBlockContainer ? "Must be" : "Is"} a{" "}
                    <b>Smart Block</b>
                    <FontAwesomeIcon icon={faBrain} style={brainStyle} />
                  </span>
                }
              />
            </Fragment>
          ) : (
            <small className="form-text text-muted">
              You cannot use this as Fallback or Smart Block because is using
              (directly or via Queries or Injects) <b>metadata not allowed</b>{" "}
              or it contains <b>Smart</b> Blocks.{" "}
              {metadataInPage?.length > 0 ? (
                <div className="metadata-in-page">
                  {metadataInPage.map((meta) => (
                    <span key={`meta-in-${meta}`} className="metadata-in-page">
                      {meta}
                    </span>
                  ))}
                </div>
              ) : null}
            </small>
          )}
        </Container>
        <Container
          title="Fallback"
          help="block-global-fallback"
          foldable={false}
          folded={false}
          className="extra-container-window"
          noMarginLast={true}
        >
          <SelectAll
            accessor="blocks"
            filters={blockSelectFilters}
            isInvalid={errors.fallback}
            value={(values.results || {}).fallbackBlockId}
            handleChange={fallbackHandleChange}
          />

          {(values.results || {}).fallbackBlockId ? (
            <div style={marginTop10}>
              <Form.Check
                type="switch"
                checked={get(values, "results.fullSubstituteFallback") || false}
                value="true"
                onChange={handleChange}
                id={`results.fullSubstituteFallback`}
                label="Full Substitute Fallback"
              />
            </div>
          ) : null}
        </Container>
      </div>
    </>
  );
}
